import { useCallback, useContext, useState } from "react";

import { AppContext } from "../../../core/context/appContextProvider";
import { getCurrentQuarter } from "../../../utils/helpers/getCurrentQuarter";
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync.hook";
import { FundFilter, InvestorFilter, ManipulatedPortalCapitalCalls } from "../../../utils/types/capitalCalls.type";
import { Quarter } from "../../../utils/types/filter.type";
import {
  download,
  getFundsFilter,
  getInvestorsFilter,
  getPortalCapitalCall,
  getQuarterList,
} from "../../capitalCalls.service";

export const usePortalCapitalCallEffect = () => {
  const [portalCapitalCall, setPortalCapitalCall] = useState<ManipulatedPortalCapitalCalls[]>();
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");
  const [quaterList, setQuarterList] = useState<Quarter[]>();
  const [fundList, setFundList] = useState<FundFilter[]>();
  const [investorList, setInvestorList] = useState<InvestorFilter[]>();
  const [transactionList, setTransactionList] = useState<{ id: string; name: string }[]>();
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { informationAlert } = useContext(AppContext);

  const fetchPortalCapitalCall = useCallback(
    async (isCanceled?: () => boolean) => {
      try {
        setIsLoading(true);

        const [fundsListResponse, investorListResponse, quarterListResponse] =
          await Promise.all([
            getFundsFilter(),
            getInvestorsFilter(),
            getQuarterList(),
          ]);

        setQuarterList(quarterListResponse);
        setFundList(fundsListResponse);
        setInvestorList(investorListResponse);
        const { quarterWithYear } = getCurrentQuarter();

        await fetchPortalCapitalCalls(quarterWithYear);
        if (isCanceled?.()) return;
      } catch (e) {
        informationAlert("Error in getting capital calls", "error");
      } finally {
        setIsLoading(false);
      }
    }, []);

  useEffectAsync(async (isCanceled) => {
    await fetchPortalCapitalCall(isCanceled);
  }, []);

  const downloadCapitalCall = async (body: any) => {
    return await download(body);
  };

  const fetchPortalCapitalCalls = async (quarterName: string) => {
    setSelectedQuarter(quarterName);

    const portalCapitalCallResponse = await getPortalCapitalCall(quarterName);

    const updatedResponse: ManipulatedPortalCapitalCalls[] = [];

    const transactionData: { id: string; name: string }[] = [];
    const documentIds: string[] = [];

    portalCapitalCallResponse.map((i) =>
      i?.funds?.map((fund: any) =>
        fund?.transactions?.map((tx: any, index: number) => {
          if (transactionData.findIndex((data) => data.id === tx.uid) === -1) {
            transactionData.push({
              id: tx.uid,
              name: tx.name === null ? "" : tx.name,
            });
            updatedResponse.push({
              ...tx,
              ...fund,
              ...i,
              fundName: fund.name,
              transactionName: tx.name,
              id: `temp-${index}-${tx.uid}`,
            });
          }

          if (tx.documentId) {
            documentIds.push(tx.documentId);
          }
        })
      )
    );

    setTransactionList(transactionData);
    setPortalCapitalCall(updatedResponse);
    setFileIds(documentIds);
  };

  return {
    selectedQuarter,
    portalCapitalCall,
    setPortalCapitalCall,
    fileIds,
    quaterList,
    fundList,
    investorList,
    transactionList,
    fetchPortalCapitalCall,
    fetchPortalCapitalCalls,
    downloadCapitalCall,
    isLoading,
  };
};
